import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaChevronDown } from 'react-icons/fa';
import { Button } from '@/components/ui/button';

export const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const isActive = (path) => location.pathname === path;

  const menuItems = {
    accueil: [
      { label: 'Mot du Président', path: '/#president' },
      { label: 'Événements', path: '/#evenements' },
      { label: 'Nos Filières', path: '/filieres' }
    ],
    apropos: [
      { label: 'Qui sommes-nous', path: '/qui-sommes-nous' },
      { label: 'Historique', path: '/qui-sommes-nous#historique' },
      { label: 'Vision et Mission', path: '/qui-sommes-nous#vision' },
      { label: 'Nos Valeurs', path: '/qui-sommes-nous#valeurs' }
    ],
    filieres: [
      { label: '— LICENCE —', path: '/filieres', isHeader: true },
      { label: 'Droit', path: '/filieres/droit' },
      { label: 'Sciences Économiques', path: '/filieres/economie-gestion' },
      { label: 'Sciences Informatiques', path: '/filieres/informatique' },
      { label: 'Marketing et Management', path: '/filieres/marketing-management' },
      { label: 'GRH', path: '/filieres/grh' },
      { label: 'Communication', path: '/filieres/communication' },
      { label: 'Logistique', path: '/filieres/logistique' },
      { label: '— MASTER —', path: '/filieres', isHeader: true },
      { label: 'Finance', path: '/filieres/finance' },
      { label: 'Droit des Affaires et Fiscalité', path: '/filieres/droit-affaires-fiscalite' },
      { label: 'Droit International et Humanitaire', path: '/filieres/droit-international-humanitaire' },
      { label: 'Marketing', path: '/filieres/marketing' },
      { label: 'Voir toutes les filières →', path: '/filieres', isFooter: true }
    ]
  };

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  return (
    <>
      {/* Top Header Bar */}
      <div className="bg-brand-secondary text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs sm:text-sm gap-2">
            <div className="flex items-center gap-4 sm:gap-6">
              <a href="tel:0712651719" className="flex items-center gap-2 hover:text-brand-accent transition-colors animate-pulse">
                <FaPhone className="text-xs text-brand-accent" />
                <span className="font-bold">07 12 65 17 19</span>
              </a>
              <a href="tel:0100959518" className="flex items-center gap-2 hover:text-brand-accent transition-colors animate-pulse">
                <FaPhone className="text-xs text-brand-accent" />
                <span className="font-bold">01 00 95 95 18</span>
              </a>
              <a href="mailto:contact@universitecefat.ci" className="hidden sm:flex items-center gap-2 hover:text-brand-accent transition-colors">
                <FaEnvelope className="text-xs" />
                <span>contact@universitecefat.ci</span>
              </a>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <a href="#" className="hover:text-brand-accent transition-colors" aria-label="Facebook">
                <FaFacebookF />
              </a>
              <a href="#" className="hover:text-brand-accent transition-colors" aria-label="Twitter">
                <FaTwitter />
              </a>
              <a href="#" className="hover:text-brand-accent transition-colors" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a href="#" className="hover:text-brand-accent transition-colors" aria-label="LinkedIn">
                <FaLinkedinIn />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="bg-brand-primary sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-uci.png" alt="Logo UCI" className="h-10 sm:h-14 w-auto" />
              <div className="hidden lg:block">
                <h1 className="text-lg sm:text-xl font-bold text-white leading-tight">Université CEFAT<br />International</h1>
              </div>
            </Link>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Desktop Navigation Links with Dropdowns */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Accueil with Dropdown */}
              <div className="relative group">
                <Link
                  to="/"
                  data-testid="nav-accueil"
                  className={`px-3 lg:px-4 py-2 text-sm lg:text-base text-white hover:bg-brand-primary-dark transition-colors rounded-md flex items-center gap-1 ${
                    isActive('/') ? 'bg-brand-primary-dark' : ''
                  }`}
                >
                  Accueil
                  <FaChevronDown className="text-xs" />
                </Link>
                <div className="absolute left-0 mt-0 w-56 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {menuItems.accueil.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-primary hover:text-white transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* À propos with Dropdown */}
              <div className="relative group">
                <Link
                  to="/qui-sommes-nous"
                  data-testid="nav-about"
                  className={`px-3 lg:px-4 py-2 text-sm lg:text-base text-white hover:bg-brand-primary-dark transition-colors rounded-md flex items-center gap-1 ${
                    isActive('/qui-sommes-nous') ? 'bg-brand-primary-dark' : ''
                  }`}
                >
                  À propos
                  <FaChevronDown className="text-xs" />
                </Link>
                <div className="absolute left-0 mt-0 w-56 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {menuItems.apropos.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-primary hover:text-white transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Filières with Dropdown */}
              <div className="relative group">
                <Link
                  to="/filieres"
                  data-testid="nav-filieres"
                  className={`px-3 lg:px-4 py-2 text-sm lg:text-base text-white hover:bg-brand-primary-dark transition-colors rounded-md flex items-center gap-1 ${
                    location.pathname.startsWith('/filieres') ? 'bg-brand-primary-dark' : ''
                  }`}
                >
                  Filières
                  <FaChevronDown className="text-xs" />
                </Link>
                <div className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-96 overflow-y-auto">
                  {menuItems.filieres.map((item, index) => (
                    item.isHeader ? (
                      <div key={index} className="px-4 py-2 text-xs font-bold text-brand-primary bg-gray-50 border-b border-gray-100">
                        {item.label}
                      </div>
                    ) : item.isFooter ? (
                      <Link
                        key={index}
                        to={item.path}
                        className="block px-4 py-3 text-sm font-semibold text-brand-primary hover:bg-brand-primary hover:text-white transition-colors border-t border-gray-100"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <Link
                        key={index}
                        to={item.path}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-primary hover:text-white transition-colors border-b border-gray-100 last:border-0"
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                data-testid="nav-contact"
                className={`px-3 lg:px-4 py-2 text-sm lg:text-base text-white hover:bg-brand-primary-dark transition-colors rounded-md ${
                  isActive('/contact') ? 'bg-brand-primary-dark' : ''
                }`}
              >
                Contact
              </Link>
            </div>

            {/* CTA Button */}
            <a href="https://scolarite-cefat.net/preinscription" className="hidden md:block">
              <Button 
                data-testid="nav-preinscription-btn"
                className="bg-white text-brand-primary hover:bg-brand-accent font-semibold px-4 lg:px-6 py-2.5 rounded-md shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 text-sm lg:text-base"
              >
                Préinscription
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-2">
                {/* Accueil Mobile */}
                <div>
                  <button
                    onClick={() => toggleDropdown('accueil')}
                    className="w-full text-left px-4 py-2 text-white hover:bg-brand-primary-dark transition-colors rounded-md flex justify-between items-center"
                  >
                    Accueil
                    <FaChevronDown className={`text-xs transition-transform ${openDropdown === 'accueil' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'accueil' && (
                    <div className="pl-4 space-y-1">
                      {menuItems.accueil.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-brand-primary-dark/50 transition-colors rounded-md"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* À propos Mobile */}
                <div>
                  <button
                    onClick={() => toggleDropdown('apropos')}
                    className="w-full text-left px-4 py-2 text-white hover:bg-brand-primary-dark transition-colors rounded-md flex justify-between items-center"
                  >
                    À propos
                    <FaChevronDown className={`text-xs transition-transform ${openDropdown === 'apropos' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'apropos' && (
                    <div className="pl-4 space-y-1">
                      {menuItems.apropos.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-brand-primary-dark/50 transition-colors rounded-md"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filières Mobile */}
                <div>
                  <button
                    onClick={() => toggleDropdown('filieres')}
                    className="w-full text-left px-4 py-2 text-white hover:bg-brand-primary-dark transition-colors rounded-md flex justify-between items-center"
                  >
                    Filières
                    <FaChevronDown className={`text-xs transition-transform ${openDropdown === 'filieres' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'filieres' && (
                    <div className="pl-4 space-y-1 max-h-64 overflow-y-auto">
                      {menuItems.filieres.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-brand-primary-dark/50 transition-colors rounded-md"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 text-white hover:bg-brand-primary-dark transition-colors rounded-md"
                >
                  Contact
                </Link>
                <a
                  href="https://scolarite-cefat.net/preinscription"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 bg-white text-brand-primary font-semibold rounded-md text-center"
                >
                  Préinscription
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};